import { useQueryClient, useJobStatus } from 'shared-ui';
import { toast } from 'react-toastify';

import { acceptTerminal, reloadTerminal } from 'api/terminals/terminals';
import { JobIdResponse } from 'api/jobs/jobs.types';
import { TerminalStatusEnum } from 'api/terminals/terminals.enum';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useMutation } from 'hooks/useMutation/useMutation';

import { TerminalDetails } from './TerminalDetails';
import { TerminalDetailsContainerProps } from './TerminalDetails.types';

export const TerminalDetailsContainer = ({
  selectedTerminal,
  setSelectedTerminal,
  terminalActionDisabled,
}: TerminalDetailsContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();

  const handleAcceptJobComplete = () => {
    queryClient.invalidateQueries([QueryKeyEnum.TERMINALS]);
    toast.dark(
      formatMessage({
        id: AppMessages['devices.details.tooltip.accept.successMessage'],
      }),
    );
    setSelectedTerminal({ ...selectedTerminal, status: TerminalStatusEnum.OK });
  };

  const handleAcceptJobError = (error: string) => {
    toast.error(formatMessage({ id: AppMessages['common.error'] }) || `Error: ${error}`);
  };

  const acceptJobStatus = useJobStatus({
    useGlobalModal: true,
    autoClose: false,
    onComplete: handleAcceptJobComplete,
    onError: handleAcceptJobError,
    showItemCount: true,
    customCompleteMessage: formatMessage({ id: AppMessages['devices.accept.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['devices.accept.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['devices.accept.result.processing'] }),
    title: formatMessage({ id: AppMessages['devices.accept.progressTitle'] }),
  });

  const { mutate: onReloadTerminal } = useMutation(reloadTerminal, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyEnum.TERMINALS]);
      toast.dark(
        formatMessage({
          id: AppMessages['devices.details.tooltip.reload.successMessage'],
        }),
      );
      setSelectedTerminal({ ...selectedTerminal, status: TerminalStatusEnum.OK });
    },
  });

  const { mutate: onAcceptTerminal } = useMutation(acceptTerminal, {
    onSuccess: (response: JobIdResponse) => {
      if (!response || !response.jobId) {
        toast.error(formatMessage({ id: AppMessages['common.error'] }));
        return;
      }
      acceptJobStatus.startJob(response.jobId);
    },
    onError: (error: unknown) => {
      const errorObj = error as Record<string, unknown>;
      toast.error(
        formatMessage({ id: AppMessages['common.error'] }) || `Error: ${errorObj?.message || 'Unknown error'}`,
      );
    },
  });

  return (
    <TerminalDetails
      selectedTerminal={selectedTerminal}
      onAcceptTerminal={onAcceptTerminal}
      onReloadTerminal={onReloadTerminal}
      terminalActionDisabled={terminalActionDisabled}
    />
  );
};
